/**
 * tds.js — shared TDS maths for every receipt screen.
 *
 * Mirrors backend/utils/tdsService.js so the % and amount a user sees match
 * what the server stores.
 *
 * Words used here
 *   cash     money that actually reached the bank / till   (+ wallet credit)
 *   TDS      tax the customer deducted (settles the bill, is NOT bank money)
 *   settle   cash + TDS  — what is knocked off a bill
 *   base     taxable value (ex-GST) the TDS is calculated on
 */
export const num = (v) => Number(v) || 0;
export const r2  = (v) => Math.round(num(v) * 100) / 100;

const parseEntries = (v) => {
  if (Array.isArray(v)) return v;
  try { return JSON.parse(v) || []; } catch { return []; }
};

const inr = (n) =>
  `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * How much of a bill is settled and how much of that is TDS (not bank money).
 *   settled : everything knocked off the bill (cash + wallet credit + TDS)
 *   tds     : the TDS part
 *   paid    : settled − tds
 */
export function billSettlement(bill) {
  const tds = r2(parseEntries(bill && bill.paymentEntries)
    .filter((e) => e.source === 'tds')
    .reduce((s, e) => s + num(e.amount), 0));
  const settled = r2(num(bill && bill.amountReceived));
  return { settled, tds, paid: r2(settled - tds) };
}

/** Payments on a bill that carry no TDS (their receipt has no 'tds' entry). */
export function earlierPaymentsWithoutTds(bill, excludeReceiptNumber = null) {
  const entries = parseEntries(bill.paymentEntries)
    .filter((e) => !(excludeReceiptNumber && e.receiptNumber === excludeReceiptNumber));
  const withTds = new Set(entries.filter((e) => e.source === 'tds' && e.receiptNumber).map((e) => e.receiptNumber));
  return r2(entries
    .filter((e) => e.source !== 'tds' && !(e.receiptNumber && withTds.has(e.receiptNumber)))
    .reduce((s, e) => s + num(e.amount), 0));
}

/**
 * Taxable value this TDS relates to for one bill.
 *   normal receipt : (cash + tds) share of the bill
 *   TDS-only       : tds + earlier payments that carried no TDS
 */
export function tdsBaseFor(bill, cash, tds, excludeReceiptNumber = null) {
  const grand = num(bill.grandTotal);
  const taxable = num(bill.taxableAmount);
  let covered = num(cash) + num(tds);
  if (num(cash) <= 0.004) covered += earlierPaymentsWithoutTds(bill, excludeReceiptNumber);
  covered = Math.min(covered, grand);
  return grand > 0 ? r2((covered * taxable) / grand) : 0;
}

/**
 * Spread `effective` (cash+credit) + `tdsTotal` over the bills and split the TDS.
 *
 *  mode 'fifo'       : oldest bill first, each up to its balance
 *  mode 'manual'     : manualSettle[billId] = amount settled on that bill (cash + TDS)
 *  mode 'manualCash' : manualSettle[billId] = money RECEIVED for that bill (cash only);
 *                      the TDS comes on top of it (typed per bill, or split from the
 *                      total so every bill carries the same TDS % of its own base)
 *
 *  perBill = true : the TDS of every bill is given in billTds[billId] (nothing is
 *                   split automatically, tdsTotal is ignored).
 *      fifo   → each bill keeps its own TDS, cash fills the rest oldest-first
 *      manual → manualSettle[billId] still means cash + TDS on that bill
 *
 * returns { rows:[{bill, balance, settle, cash, tds, base}], base, surplus,
 *           totalSettle, cashAllocated }
 */
export function planAllocation({
  bills, effective, tdsTotal, mode = 'fifo', manualSettle = {}, balanceOf = null, excludeRn = null,
  perBill = false, billTds = {},
}) {
  const balOf = (bill) => Math.max(0, r2(balanceOf ? balanceOf(bill) : num(bill.grandTotal) - num(bill.amountReceived)));
  let rows;

  if (perBill) {
    let cashLeft = Math.max(0, r2(effective));
    rows = bills.map((bill) => {
      const balance = balOf(bill);
      const want = Math.max(0, r2(billTds && billTds[bill.id]));
      let settle;
      let tds;
      if (mode === 'manual') {
        settle = Math.min(num(manualSettle[bill.id]), balance);
        tds = Math.min(want, settle);
      } else if (mode === 'manualCash') {
        const cash = Math.min(Math.max(0, num(manualSettle[bill.id])), balance);
        tds = Math.min(want, r2(balance - cash));
        settle = r2(cash + tds);
      } else {
        tds = Math.min(want, balance);
        const cash = Math.min(cashLeft, r2(balance - tds));
        cashLeft = Math.max(0, r2(cashLeft - cash));
        settle = r2(cash + tds);
      }
      return { bill, balance, settle: r2(settle), cash: 0, tds: r2(tds), base: 0 };
    });
  } else if (mode === 'manualCash') {
    // money received per bill is typed; the TDS is added on top of it
    const T = Math.max(0, r2(tdsTotal));
    rows = bills.map((bill) => {
      const balance = balOf(bill);
      const cash = Math.min(Math.max(0, num(manualSettle[bill.id])), balance);
      return { bill, balance, settle: r2(cash), cash: 0, tds: 0, base: 0 };
    });
    const cashes = rows.map((r) => r.settle);
    const rooms  = rows.map((r) => Math.max(0, r2(r.balance - r.settle)));
    const rate   = (r) => { const g = num(r.bill.grandTotal); return g > 0 ? num(r.bill.taxableAmount) / g : 0; };
    const sum    = (a) => a.reduce((x, y) => x + y, 0);
    const cashSum = sum(cashes);
    let tdsList = rows.map(() => 0);

    if (T > 0) {
      if (cashSum <= 0.004) {
        // TDS-only: fill the bills oldest first
        let left = T;
        rows.forEach((r, i) => {
          const t = Math.min(rooms[i], left);
          tdsList[i] = r2(t);
          left = Math.max(0, r2(left - t));
        });
      } else {
        // every bill carries the same TDS rate λ on its own base:  tds = λ·r·(cash + tds)
        const tdsAt = (lam) => rows.map((r, i) => {
          if (cashes[i] <= 0) return 0;
          const k = lam * rate(r);
          return Math.min(rooms[i], (k * cashes[i]) / (1 - k));
        });
        const MAXLAM = 0.999;
        if (sum(tdsAt(MAXLAM)) <= T) {
          tdsList = tdsAt(MAXLAM).map(r2);
        } else {
          let lo = 0; let hi = MAXLAM;
          for (let k = 0; k < 60; k += 1) {
            const mid = (lo + hi) / 2;
            if (sum(tdsAt(mid)) < T) lo = mid; else hi = mid;
          }
          tdsList = tdsAt(hi).map(r2);
        }
        // cents lost to rounding → bills that have cash
        const target = Math.min(T, sum(rooms.map((x, i) => (cashes[i] > 0 ? x : 0))));
        let left = r2(target - sum(tdsList));
        for (let i = 0; i < rows.length && Math.abs(left) > 0.004; i += 1) {
          if (cashes[i] <= 0) continue;
          if (left > 0) {
            const add = Math.min(r2(rooms[i] - tdsList[i]), left);
            tdsList[i] = r2(tdsList[i] + add); left = r2(left - add);
          } else {
            const sub = Math.min(tdsList[i], -left);
            tdsList[i] = r2(tdsList[i] - sub); left = r2(left + sub);
          }
        }
      }
    }
    rows.forEach((r, i) => { r.tds = tdsList[i]; r.settle = r2(cashes[i] + tdsList[i]); });
  } else {
    const T = Math.max(0, r2(tdsTotal));
    const total = r2(num(effective) + T);

    let rem = total;
    rows = bills.map((bill) => {
      const balance = balOf(bill);
      let settle;
      if (mode === 'manual') settle = Math.min(num(manualSettle[bill.id]), balance);
      else { settle = Math.min(rem, balance); rem = Math.max(0, r2(rem - settle)); }
      return { bill, balance, settle: r2(settle), cash: 0, tds: 0, base: 0 };
    });

    const settleSum = r2(rows.reduce((s, r) => s + r.settle, 0));
    const tdsToPlace = Math.min(T, settleSum);

    if (tdsToPlace > 0) {
      if (num(effective) <= 0.004) {
        // TDS-only: everything settled is TDS
        rows.forEach((r) => { r.tds = r.settle; });
      } else {
        // proportional to the taxable value each bill's settlement covers
        const w = rows.map((r) => {
          const g = num(r.bill.grandTotal);
          return g > 0 ? (r.settle * num(r.bill.taxableAmount)) / g : 0;
        });
        const wSum = w.reduce((s, x) => s + x, 0);
        rows.forEach((r, i) => {
          const share = wSum > 0 ? r2((tdsToPlace * w[i]) / wSum) : 0;
          r.tds = Math.min(r.settle, share);
        });
        // rounding / cap leftovers → bills that still have room
        let left = r2(tdsToPlace - rows.reduce((s, r) => s + r.tds, 0));
        for (const r of rows) {
          if (left <= 0.004) break;
          const room = r2(r.settle - r.tds);
          const add = Math.min(room, left);
          r.tds = r2(r.tds + add);
          left = r2(left - add);
        }
      }
    }
  }

  rows.forEach((r) => {
    r.cash = r2(r.settle - r.tds);
    r.base = r.tds > 0 ? tdsBaseFor(r.bill, r.cash, r.tds, excludeRn) : 0;
  });

  const cashAllocated = r2(rows.reduce((s, r) => s + r.cash, 0));
  return {
    rows,
    base: r2(rows.reduce((s, r) => s + r.base, 0)),
    totalSettle: r2(rows.reduce((s, r) => s + r.settle, 0)),
    cashAllocated,
    surplus: Math.max(0, r2(num(effective) - cashAllocated)),
  };
}

/**
 * Turn what the user typed into the final TDS amount / % / allocation plan.
 *
 * input = { edited: 'percent' | 'amount' | null, percent: '', amount: '',
 *           perBill?: boolean, billTds?: { [billId]: '123.45' } }
 *   - the field the user touched last is the source of truth
 *   - the other one is calculated from it (amount ÷ base, or % × base)
 *   - perBill: the TDS of each bill is typed by hand (billTds), the total and
 *     the overall % are calculated from those
 *
 * On top of { amount, percent, base, plan } the result carries checks the forms show:
 *   error       text → TDS cannot be saved as entered (block Save). Includes a typed amount
 *               that is more than can be applied — it is never trimmed silently
 *   clamped     { requested, applied } → details of that case
 *   overflow    ₹ that will spill into the wallet because payment + TDS > balance
 *   leftover    [{billNumber,left,base}] bills still owing after this receipt although
 *               TDS was entered (TDS is only worked out on what this receipt settles)
 *   effective, totalBalance, cashNeeded   numbers used in the messages
 */
export function resolveTds({ bills, effective, mode, manualSettle, balanceOf, excludeRn, input }) {
  const common = { bills, effective, mode, manualSettle, balanceOf, excludeRn };
  const core = resolveCore(common, input);
  return decorate({ bills, effective, excludeRn, input }, core);
}

function resolveCore(common, input) {
  const none = () => {
    const plan = planAllocation({ ...common, tdsTotal: 0 });
    return { amount: 0, percent: null, base: 0, plan };
  };

  // TDS typed bill by bill
  if (input && input.perBill) {
    const plan = planAllocation({ ...common, tdsTotal: 0, perBill: true, billTds: input.billTds || {} });
    const placed = r2(plan.rows.reduce((s, r) => s + r.tds, 0));
    return { amount: placed, percent: plan.base > 0 && placed > 0 ? (placed / plan.base) * 100 : null, base: plan.base, plan };
  }

  if (!input || !input.edited) return none();

  if (input.edited === 'amount') {
    const amount = Math.max(0, r2(input.amount));
    if (!amount) return none();
    const plan = planAllocation({ ...common, tdsTotal: amount });
    const placed = r2(plan.rows.reduce((s, r) => s + r.tds, 0));
    return { amount: placed, percent: plan.base > 0 ? (placed / plan.base) * 100 : null, base: plan.base, plan };
  }

  // percent → amount (the base depends on the amount, so iterate; converges fast)
  const p = Math.max(0, num(input.percent));
  if (!p) return none();
  // seed with a token amount: with TDS = 0 there is no base to work from yet
  let T = 0.01;
  let plan = planAllocation({ ...common, tdsTotal: T });
  for (let i = 0; i < 20; i += 1) {
    plan = planAllocation({ ...common, tdsTotal: T });
    const next = r2((p / 100) * plan.base);
    if (Math.abs(next - T) < 0.005) { T = next; break; }
    T = next;
  }
  plan = planAllocation({ ...common, tdsTotal: T });
  const placed = r2(plan.rows.reduce((s, r) => s + r.tds, 0));
  return { amount: placed, percent: p, base: plan.base, plan };
}

/** Adds error / clamped / overflow / leftover to a resolved TDS result. */
function decorate({ bills, effective, excludeRn, input }, res) {
  const eff = Math.max(0, r2(effective));
  const plan = res.plan;
  const perBill = !!(input && input.perBill);
  const list = bills || [];

  // did the user actually ask for TDS?
  let asked = false;
  let requested = 0;
  if (input) {
    if (perBill) {
      requested = r2(list.reduce((s, b) => s + Math.max(0, num(input.billTds && input.billTds[b.id])), 0));
      asked = requested > 0;
    } else if (input.edited === 'amount') {
      requested = Math.max(0, r2(input.amount));
      asked = requested > 0;
    } else if (input.edited === 'percent') {
      asked = num(input.percent) > 0;
    }
  }

  // 3) typed amount is more than can be applied
  const clamped = !perBill && input && input.edited === 'amount' && requested > res.amount + 0.009
    ? { requested, applied: res.amount }
    : null;

  // 2) TDS that cannot be saved (the server would reject it)
  let error = null;
  if (asked && list.length) {
    const earlier = list.reduce((s, b) => s + earlierPaymentsWithoutTds(b, excludeRn), 0);
    if (eff <= 0.004 && earlier <= 0.004) {
      error = list.length > 1
        ? 'TDS-only works only after the payment is already recorded on these bills. Enter the amount received first.'
        : 'TDS-only works only after the payment is already recorded on this bill. Enter the amount received first.';
    } else {
      const bad = plan.rows.find((r) => r.tds > r.base + 0.011);
      if (bad) {
        error = `TDS ${inr(bad.tds)} on ${bad.bill.billNumber} is more than the taxable value it can be calculated on (${inr(bad.base)}). Reduce the TDS or enter the payment received.`;
      }
    }
  }

  // typed TDS is more than can be applied → do not save a different amount silently: make the user fix it
  if (!error && clamped) {
    error = `TDS ${inr(clamped.requested)} is more than the ${inr(clamped.applied)} that this payment and the bill balance allow. Enter ${inr(clamped.applied)} or less.`;
  }
  // same rule for TDS typed bill by bill
  if (!error && perBill) {
    const over = plan.rows.find((r) => Math.max(0, num(input.billTds && input.billTds[r.bill.id])) > r.tds + 0.009);
    if (over) {
      const typed = Math.max(0, r2(input.billTds[over.bill.id]));
      error = `TDS ${inr(typed)} on ${over.bill.billNumber} is more than the ${inr(over.tds)} that can be applied to it. Enter ${inr(over.tds)} or less.`;
    }
  }

  // 1) payment + TDS is more than the bills need → the extra would spill into the wallet
  const totalBalance = r2(plan.rows.reduce((s, r) => s + r.balance, 0));
  // (compares with the bills' total balance, so an unallocated amount in manual split is not counted)
  const overflow = res.amount > 0.004 ? Math.max(0, r2(eff + res.amount - totalBalance)) : 0;

  // 5) TDS is only worked out on what this receipt settles → a small balance can be left
  const leftover = plan.rows
    .filter((r) => r.tds > 0 && earlierPaymentsWithoutTds(r.bill, excludeRn) > 0.004 && r2(r.balance - r.settle) > 0.01)
    .map((r) => ({ billNumber: r.bill.billNumber, left: r2(r.balance - r.settle), base: r.base }));

  return {
    ...res,
    error,
    clamped,
    overflow,
    leftover,
    effective: eff,
    totalBalance,
    cashNeeded: Math.max(0, r2(eff - overflow)),
    perBill,
  };
}

/**
 * Text for "payment + TDS is more than the bills need".
 *   mode 'block'  : single bill — Save is blocked
 *   mode 'wallet' : several bills — allowed, but the extra goes to the wallet
 */
export function overflowMessage(res, mode = 'wallet') {
  if (!res || !(res.amount > 0) || !(res.overflow > 0.01)) return '';
  const sum = r2(res.effective + res.amount);
  const head = `Payment ${inr(res.effective)} + TDS ${inr(res.amount)} = ${inr(sum)}, which is ${inr(res.overflow)} more than the balance (${inr(res.totalBalance)}).`;
  const hint = `If the customer deducted TDS, enter ${inr(res.cashNeeded)} as the amount received.`;
  if (mode === 'block') return `${head} ${hint} Save is blocked until it fits.`;
  return `${head} The extra ${inr(res.overflow)} will be added to the customer's wallet and recorded as received. ${hint}`;
}

/** Allocations in the shape POST/PUT /client-receipts expects. */
export function allocationsPayload(plan) {
  return plan.rows.map((r) => ({
    billId: r.bill.id,
    billNumber: r.bill.billNumber,
    allocatedAmount: r.cash,
    tdsAmount: r.tds,
  }));
}

/** Extra receipt fields to merge into the POST/PUT body. */
export function tdsPayload(resolved, input, section) {
  return {
    tdsAmount: resolved.amount,
    tdsPercent: resolved.amount > 0 && input && !input.perBill && input.edited === 'percent' ? num(input.percent) : undefined,
    tdsSection: resolved.amount > 0 ? (section || '').trim() : '',
  };
}

/** "2" , "1.97" — percent text for display (max 2 decimals, no trailing zeros). */
export const fmtPercent = (p) => (p === null || p === undefined || Number.isNaN(Number(p)) ? '' : String(Number(Number(p).toFixed(2))));

/** Financial year ('2025-26') of a YYYY-MM-DD date. */
export function fyOf(dateStr) {
  const y = Number(String(dateStr).slice(0, 4));
  const m = Number(String(dateStr).slice(5, 7));
  if (!y || !m) return '';
  const start = m >= 4 ? y : y - 1;
  return `${start}-${String((start + 1) % 100).padStart(2, '0')}`;
}